import { useApi } from "./api"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Network, Token } from "@/config/tokens"

export interface WebhookConfig {
  id: string;
  url: string;
  secret: string;
}

export interface WebhookConfigInput {
  url: string;
  secret: string;
}

export interface WebhookLog {
  id: string;
  status: "PENDING" | "SUCCESS" | "FAILED" | "RETRYING";
  attempts: number;
  error?: string;
  createdAt: string;
  updatedAt: string;
}

export interface WebhookTestResult {
  success: boolean;
  webhookCall: {
    id: string;
    status: "PENDING" | "SUCCESS" | "FAILED" | "RETRYING";
    error?: string;
    response?: Record<string, unknown>;
  };
}

export interface WebhookCall {
  id: string;
  webhookId: string;
  payload: WebhookPayload;
  signature: string;
  status: "PENDING" | "SUCCESS" | "FAILED" | "RETRYING";
  attempts: number;
  error?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface WebhookPayload {
  paymentLinkID: string;
  paymentID: string;
  fromAddress: string;
  toAddress: string;
  hash: string;
  network: Network;
  token: Token;
  amount: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  Payer_Email: string;
}

export function useWebhook() {
  const { get, post, remove } = useApi()
  const queryClient = useQueryClient()

  const { data: webhook, isLoading } = useQuery({
    queryKey: ["webhook"],
    queryFn: () => get("webhook"),
    retry: false, // Don't retry if webhook doesn't exist
  })

  const { mutateAsync: createWebhook, isError: createError } = useMutation({
    mutationFn: (data: WebhookConfigInput) => post("webhook", data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["webhook"] }),
  })

  const { mutateAsync: deleteWebhook } = useMutation({
    mutationFn: () => remove("webhook"),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["webhook"] }),
  })

  const { mutateAsync: testWebhook } = useMutation({
    mutationFn: () => post("webhook/test", {}),
  })

  return {
    webhook,
    createWebhook,
    deleteWebhook,
    testWebhook,
    isLoading,
    createError,
  }
}

export function useWebhookLogs() {
  const { get } = useApi()

  const { data: logs = [] } = useQuery<WebhookLog[]>({
    queryKey: ["webhook-logs"],
    queryFn: () => get("webhook/logs"),
  })

  return { logs }
}
